import { useState, createContext, useContext } from 'react';
import React from 'react';
import { ReserveOptions } from '@/components/reserve/Reserve/types';
import { ISchedule, Office } from '@gettactic/api';

export type ReserveModalData =
  | (ReserveOptions & { reserveModal: boolean })
  | null;

export type ReserveEditModalData = {
  editModal: boolean;
  schedule: ISchedule;
  canRespondCancel?: boolean;
  office?: Office;
} | null;

export type ReservationCancelData = {
  open: boolean;
  schedules: ISchedule[];
} | null;

interface ReserveModalContextType {
  reserveModalData: ReserveModalData;
  setReserveModalData: (data: ReserveModalData) => void;

  editModalData: ReserveEditModalData;
  setEditModalData: (data: ReserveEditModalData) => void;

  cancelModalData: ReservationCancelData;
  setCancelModalData: (data: ReservationCancelData) => void;

  openEditModal: (schedule: ISchedule, canRespondCancel?: boolean, office?: Office) => void;
  openCancelModal: (schedules: ISchedule[]) => void;
  closeAllModals: () => void;
}

const ReserveModalContext = createContext<ReserveModalContextType | undefined>(
  undefined
);

export function ReserveModalProvider({
  children
}: { children: React.ReactNode }) {
  const [reserveModalData, setReserveModalData] =
    useState<ReserveModalData>(null);
  const [editModalData, setEditModalData] =
    useState<ReserveEditModalData>(null);
  const [cancelModalData, setCancelModalData] =
    useState<ReservationCancelData>(null);

  const openEditModal = (schedule: ISchedule, canRespondCancel = true, office?: Office) => {
    setEditModalData({
      editModal: true,
      schedule,
      canRespondCancel,
      office
    });
  };

  const openCancelModal = (schedules: ISchedule[]) => {
    setCancelModalData({
      open: true,
      schedules
    });
  };

  const closeAllModals = () => {
    setReserveModalData(null);
    setEditModalData(null);
    setCancelModalData(null);
  };

  const value = {
    reserveModalData,
    setReserveModalData,
    editModalData,
    setEditModalData,
    cancelModalData,
    setCancelModalData,
    openEditModal,
    openCancelModal,
    closeAllModals
  };

  return React.createElement(ReserveModalContext.Provider, { value }, children);
}

export function useReserveModal() {
  const context = useContext(ReserveModalContext);

  if (context === undefined) {
    throw new Error(
      'useReserveModal must be used within a ReserveModalProvider'
    );
  }

  return context;
}
